import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Create Discount Group From Extraction Step (Discounts v2).
 *
 * Turns an extracted Discount Request form into a discount group,
 * mirroring the PO-intake resolution flow:
 *   - the extracted distributor name (+ the sender email, when the inbound
 *     email is wired) is matched to a Customer (auto-bind vs suggestion);
 *   - each line's Item P/N is matched to a product variant (exact SKU, then
 *     the shared product matcher, sellable-only).
 *
 * Fully-resolved groups are submitted for approval immediately (emits
 * DISCOUNT_GROUP_SUBMITTED → the group-approval workflow raises the task).
 * Groups with an unresolved customer or unresolved lines stay DRAFT with
 * `metadata.review` populated for the group review screen
 * (/discounts-v2/$groupId); their ids come out on `needsReviewGroupIds` so the
 * canvas can wire a Create Task node for the reviewer.
 *
 * Expected extraction shape (tolerant — see the handler): header
 * customer/distributor name, end customer, project description; a
 * `line_items` array with per-row sku, item description, quantity, and
 * requested price. The "Discount Request" stored schema (seeded by
 * `backend/src/scripts/discounts-v2/seedDiscountRequestSchema.ts`) emits
 * exactly these fields.
 *
 * Backend-only (DB access); handler in
 * backend/src/workflows/steps/accounts-receivable/.
 */
export const createDiscountGroupFromExtractionStep = defineStepConfig({
  type: "create_discount_group_from_extraction",
  name: "Create Discount Group From Extraction",
  description:
    "Creates a Discounts v2 group from an extracted Discount Request form, matching the customer and products; submits for approval or flags for review",
  category: "Data Access",
  libraryModule: "OrderManagement",
  inputSchema: z.object({
    documentOcrResults: z
      .array(
        z.object({
          document: entityOfType(SystemEntity.CORE_DOCUMENT),
          ocrResult: entityOfType(
            SystemEntity.CORE_DOCUMENT_OCR_RESULT,
          ).optional(),
          status: z.enum(["completed", "failed", "skipped"]),
        }),
      )
      .describe("Extraction results from the Extract Document Content step"),
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL)
      .optional()
      .describe(
        "The source email (optional). The sender address strengthens customer matching.",
      ),
  }),

  outputSchema: z.object({
    discountGroupIds: z
      .array(z.string().uuid())
      .describe(
        "Ids of the discount groups created (one per discount request document)",
      ),
    submittedGroupIds: z
      .array(z.string().uuid())
      .describe("Groups fully resolved and submitted for approval"),
    needsReviewGroupIds: z
      .array(z.string().uuid())
      .describe(
        "Groups left in DRAFT with an unresolved customer or unresolved lines — wire to a Create Task step to alert a reviewer",
      ),
    createdCount: z.number().int(),
    unresolvedLineCount: z
      .number()
      .int()
      .describe("Total extracted rows that could not be resolved"),
  }),

  configSchema: z.object({
    defaultGroupNamePrefix: z
      .string()
      .default("DR")
      .describe(
        "Prefix for the generated group name (followed by the extracted reference code or document name)",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
    },
  },
});
